import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Search, KanbanSquare, Building2, Users, FileText,
  GraduationCap, ScanLine, Mails, CalendarClock, BarChart3, LifeBuoy,
  Settings as SettingsIcon, Package, LogOut, Command, Menu, Bot, ShieldCheck,
  Eye, UserCheck, XCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import CommandPalette from "@/components/CommandPalette";
import AIInspector from "@/components/AIInspector";
import ThemeSelector from "@/components/ThemeSelector";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/discover", label: "Discover", icon: Search },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/career", label: "Career Master", icon: GraduationCap },
  { to: "/resumes", label: "Resume Studio", icon: FileText },
  { to: "/ats", label: "ATS Lab", icon: ScanLine },
  { to: "/cover-letters", label: "Cover Letters", icon: Mails },
  { to: "/interviews", label: "Interviews", icon: CalendarClock },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/help", label: "Help Center", icon: LifeBuoy },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout({ children }) {
  const { user, actAsUser, switchWorkspaceUser, logout } = useAuth();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [inspector, setInspector] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const nav = useNavigate();

  const isRoot = user?.role === "root" || Boolean(user?.is_root_impersonating);
  const isAdmin = user?.role === "admin" || user?.role === "root" || Boolean(user?.is_root_impersonating);

  useEffect(() => {
    if (isRoot) {
      api.get("/admin/users").then((r) => setAllUsers(r.data)).catch(() => {});
    }
  }, [isRoot]);


  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-64 flex-shrink-0 border-r border-border bg-card flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          mobileNav ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-extrabold">
            E
          </div>
          <span className="text-lg font-extrabold tracking-tight">EzJOB!</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileNav(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <div className="pt-3 mt-3 border-t border-border space-y-0.5">
              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Admin
              </p>
              <NavLink to="/admin/package" onClick={() => setMobileNav(false)} data-testid="nav-admin-package"
                className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                <Package className="h-4 w-4" /> Package & Download
              </NavLink>
              <NavLink to="/admin/users" onClick={() => setMobileNav(false)} data-testid="nav-admin-users"
                className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                <ShieldCheck className="h-4 w-4" /> Users & Diagnostics
              </NavLink>
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <button onClick={logout} title="Sign out" data-testid="logout-btn"
              className="text-muted-foreground hover:text-destructive transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {mobileNav && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileNav(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 lg:pl-64">
        <header className="h-16 flex items-center gap-3 px-4 md:px-6 border-b border-border bg-card/80 backdrop-blur sticky top-0 z-20">
          <button className="lg:hidden text-muted-foreground" onClick={() => setMobileNav(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <button
            onClick={() => setPaletteOpen(true)}
            data-testid="command-palette-trigger"
            className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-full max-w-sm"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search jobs, apps…</span>
            <kbd className="hidden sm:flex items-center gap-1 text-xs font-mono border border-border rounded px-1.5 py-0.5">
              <Command className="h-3 w-3" />K
            </kbd>
          </button>

          {/* Root User Navigation Matrix */}
          {isRoot && (
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs">
              <Eye className="h-3.5 w-3.5 text-amber-500" />
              <span className="font-semibold text-amber-500 hidden md:inline">Viewing As:</span>
              <select
                value={actAsUser?.id || ""}
                onChange={(e) => {
                  const targetId = e.target.value;
                  if (!targetId) {
                    switchWorkspaceUser(null);
                  } else if (targetId === "all") {
                    switchWorkspaceUser({ id: "all", name: "See all (All Users)", role: "all" });
                  } else {
                    const match = allUsers.find((u) => u.id === targetId);
                    switchWorkspaceUser(match || { id: targetId });
                  }
                }}
                data-testid="viewing-as-select"
                className="bg-transparent font-medium border-0 text-foreground text-xs focus:ring-0 cursor-pointer py-0.5"
              >
                <option value="" className="bg-card text-foreground">Root (Self)</option>
                <option value="all" className="bg-card text-foreground">See all</option>
                {allUsers
                  .filter((u) => u.role !== "root")
                  .map((u) => (
                    <option key={u.id} value={u.id} className="bg-card text-foreground">
                      {u.name || u.email} ({u.role})
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Root Impersonation Active Badge & Return Button (Compact in Header Navbar) */}
          {isRoot && actAsUser && (
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/40 text-xs font-medium text-amber-400 shrink-0">
              <UserCheck className="h-3.5 w-3.5 text-amber-400 shrink-0" />
              <span className="truncate max-w-[280px]" title={actAsUser.id === "all" ? "Global Support Mode (See All)" : `Impersonating ${actAsUser.name || actAsUser.email}. All actions apply directly.`}>
                {actAsUser.id === "all" ? (
                  <span><strong>Global Support:</strong> See All</span>
                ) : (
                  <span><strong>Impersonating:</strong> {actAsUser.name || actAsUser.email}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => switchWorkspaceUser(null)}
                className="inline-flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 hover:text-amber-200 border border-amber-500/50 px-1.5 py-0.5 rounded text-[11px] font-semibold transition-all ml-1 shrink-0"
                title="Return to Root Administrator account"
              >
                <XCircle className="h-3 w-3" /> Return to Root
              </button>
            </div>
          )}

          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => setInspector((v) => !v)} data-testid="ai-inspector-toggle">
            <Bot className="h-4 w-4 mr-1" /> AI
          </Button>
          <ThemeSelector />
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8" data-testid="main-content">
          <div className="animate-fade-up max-w-7xl mx-auto">{children}</div>
        </main>

      </div>

      {inspector && <AIInspector onClose={() => setInspector(false)} />}
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} navigate={nav} isAdmin={isAdmin} />
    </div>
  );
}
